// src/pages/ShopSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { Save, Upload, Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { shopSettingsApi, uploadApi } from '../api';

const ShopSettingsPage = () => {
  const { storeName: ctxStoreName, setShopSettings } = useApp();

  const [settings, setSettings] = useState({
    shop_name: '', shop_name_en: '', shop_name_km: '',
    shop_tag: '', shop_tag_en: '', shop_tag_km: '',
    logo_url: '', primary_color: '#F97316',
    phone: '', email: '', address: '', facebook: '', telegram: '',
    enable_pickup: 1, enable_delivery: 1,
    khr_rate: 4200
  });
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerForm, setBannerForm] = useState({ image_url: '', title: '', link_url: '', sort_order: 0 });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await shopSettingsApi.getSettings();
      if (res.success && res.data) {
        const s = res.data;
        setSettings({
          shop_name:       s.shop_name      || '',
          shop_name_en:    s.shop_name_en   || '',
          shop_name_km:    s.shop_name_km   || '',
          shop_tag:        s.shop_tag       || '',
          shop_tag_en:     s.shop_tag_en    || '',
          shop_tag_km:     s.shop_tag_km    || '',
          logo_url:        s.logo_url       || '',
          primary_color:   s.primary_color  || '#F97316',
          phone:           s.phone          || '',
          email:           s.email          || '',
          address:         s.address        || '',
          facebook:        s.facebook       || '',
          telegram:        s.telegram       || '',
          enable_pickup:   s.enable_pickup  !== undefined ? s.enable_pickup  : 1,
          enable_delivery: s.enable_delivery !== undefined ? s.enable_delivery : 1,
          khr_rate:        s.khr_rate       || 4200,
        });
        if (s.logo_url) setLogoPreview(s.logo_url);
        setBanners(s.banners || []);
      }
    } catch (err) {
      setError('加载设置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('图片不能超过 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return settings.logo_url;
    setUploadingLogo(true);
    try {
      const res = await uploadApi.uploadProductImage(logoFile);
      if (res.success && res.data?.url) return res.data.url;
      throw new Error(res.message || '上传失败');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!settings.shop_name) { setError('店铺名称不能为空'); return; }
    if (!settings.khr_rate || settings.khr_rate < 100) { setError('KHR 汇率不能小于 100'); return; }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let logoUrl = settings.logo_url;
      if (logoFile) logoUrl = await uploadLogo();

      const res = await shopSettingsApi.updateSettings({ ...settings, logo_url: logoUrl });
      if (res.success) {
        setSuccess('保存成功！');
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo_url: logoUrl }));
        if (setShopSettings) setShopSettings(res.data);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleAddBanner = async () => {
    if (!bannerFile && !bannerForm.image_url) { alert('请上传广告图片'); return; }
    setSavingBanner(true);
    try {
      let imageUrl = bannerForm.image_url;
      if (bannerFile) {
        setUploadingBanner(true);
        const res = await uploadApi.uploadProductImage(bannerFile);
        setUploadingBanner(false);
        if (res.success && res.data?.url) imageUrl = res.data.url;
        else throw new Error(res.message || '上传失败');
      }
      const res = await shopSettingsApi.createBanner({ ...bannerForm, image_url: imageUrl });
      if (res.success) {
        setBanners(prev => [...prev, res.data]);
        setShowBannerForm(false);
        setBannerForm({ image_url: '', title: '', link_url: '', sort_order: 0 });
        setBannerFile(null);
        setBannerPreview('');
      } else {
        alert(res.message || '添加失败');
      }
    } catch (err) {
      alert(err.message || '添加失败');
    } finally {
      setSavingBanner(false);
      setUploadingBanner(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('确定删除这张广告图吗？')) return;
    try {
      const res = await shopSettingsApi.deleteBanner(id);
      if (res.success) setBanners(prev => prev.filter(b => b.id !== id));
      else alert(res.message || '删除失败');
    } catch (err) { alert(err.message || '删除失败'); }
  };

  const toggleBannerStatus = async (banner) => {
    const newStatus = banner.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await shopSettingsApi.updateBanner(banner.id, { status: newStatus });
      if (res.success) {
        setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, status: newStatus } : b));
      }
    } catch (err) { alert('操作失败'); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center min-h-screen">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">商家设置</h2>
          <p className="text-gray-600 mt-1">管理店铺基本信息、Logo 和广告图</p>
        </div>
        <button onClick={handleSave} disabled={saving || uploadingLogo}
          className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" />
          <span>{saving ? '保存中...' : '保存设置'}</span>
        </button>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：Logo + 主题色 */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">店铺 Logo</h3>
            <div className="flex flex-col items-center space-y-4">
              <div className="w-32 h-32 rounded-full border-4 border-dashed border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50">
                {logoPreview
                  ? <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-12 h-12 text-gray-400" />}
              </div>
              <input type="file" id="logoUpload" accept="image/*" onChange={handleLogoChange} className="hidden" />
              <label htmlFor="logoUpload"
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm">
                <Upload className="w-4 h-4" />
                <span>上传 Logo</span>
              </label>
              <p className="text-xs text-gray-400">建议正方形，最大 5MB</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">主题色</h3>
            <div className="flex items-center space-x-3">
              <input type="color" value={settings.primary_color}
                onChange={e => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300" />
              <div>
                <p className="text-sm font-medium">{settings.primary_color}</p>
                <p className="text-xs text-gray-400">H5 端主题色</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {['#F97316', '#EF4444', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'].map(color => (
                <button key={color}
                  onClick={() => setSettings(prev => ({ ...prev, primary_color: color }))}
                  className={`w-8 h-8 rounded-full border-2 ${settings.primary_color === color ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: color }} />
              ))}
            </div>
          </div>
        </div>

        {/* 右侧：店铺信息 */}
        <div className="lg:col-span-2 space-y-6">

          {/* 店铺名称 */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">店铺名称</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">中文名 *</label>
                <input type="text" value={settings.shop_name}
                  onChange={e => setSettings(prev => ({ ...prev, shop_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="台灣柑仔店" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">英文名</label>
                <input type="text" value={settings.shop_name_en}
                  onChange={e => setSettings(prev => ({ ...prev, shop_name_en: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Taiwan Shop" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">高棉语名</label>
                <input type="text" value={settings.shop_name_km}
                  onChange={e => setSettings(prev => ({ ...prev, shop_name_km: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* 店铺标语 */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">店铺标语</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">中文</label>
                <input type="text" value={settings.shop_tag}
                  onChange={e => setSettings(prev => ({ ...prev, shop_tag: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="台灣食品專賣店" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">英文</label>
                <input type="text" value={settings.shop_tag_en}
                  onChange={e => setSettings(prev => ({ ...prev, shop_tag_en: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Taiwan Food Specialty" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">高棉语</label>
                <input type="text" value={settings.shop_tag_km}
                  onChange={e => setSettings(prev => ({ ...prev, shop_tag_km: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* 配送方式 */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">配送方式</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">外送配送</p>
                  <p className="text-xs text-gray-400 mt-0.5">开启后 H5 用户可选择外送</p>
                </div>
                <div
                  onClick={() => setSettings(prev => ({ ...prev, enable_delivery: prev.enable_delivery ? 0 : 1 }))}
                  className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors flex-shrink-0 ${settings.enable_delivery ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${settings.enable_delivery ? 'left-6' : 'left-1'}`} />
                </div>
              </div>
              <div className="border-t pt-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">到店自提</p>
                  <p className="text-xs text-gray-400 mt-0.5">开启后 H5 用户可选择到店自提</p>
                </div>
                <div
                  onClick={() => setSettings(prev => ({ ...prev, enable_pickup: prev.enable_pickup ? 0 : 1 }))}
                  className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors flex-shrink-0 ${settings.enable_pickup ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${settings.enable_pickup ? 'left-6' : 'left-1'}`} />
                </div>
              </div>
            </div>
          </div>

          {/* KHR 汇率 */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-1">支付宝收款汇率</h3>
            <p className="text-sm text-gray-500 mb-4">
              收银台选择支付宝（KHR）收款时，USD 金额 × 汇率 = 实际收取瑞尔金额
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  1 USD = ? KHR
                </label>
                <input
                  type="number"
                  min="100"
                  max="10000"
                  step="50"
                  value={settings.khr_rate}
                  onChange={e => setSettings(prev => ({ ...prev, khr_rate: parseInt(e.target.value) || 4200 }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-lg font-medium"
                />
              </div>
              <div className="flex-shrink-0 pt-6">
                <div className="flex gap-2">
                  {[4000, 4100, 4200, 4300].map(rate => (
                    <button key={rate}
                      onClick={() => setSettings(prev => ({ ...prev, khr_rate: rate }))}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        settings.khr_rate === rate
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                      }`}>
                      {rate}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600">
                示例：商品 $10.00 USD，按当前汇率收取
                <span className="font-bold ml-1">
                  {(10 * (settings.khr_rate || 4200)).toLocaleString()} ៛
                </span>
              </p>
            </div>
          </div>

          {/* 联系方式 */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold mb-4">联系方式</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">电话</label>
                <input type="text" value={settings.phone}
                  onChange={e => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">邮箱</label>
                <input type="email" value={settings.email}
                  onChange={e => setSettings(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">地址</label>
                <input type="text" value={settings.address}
                  onChange={e => setSettings(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Facebook</label>
                <input type="text" value={settings.facebook}
                  onChange={e => setSettings(prev => ({ ...prev, facebook: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://facebook.com/..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Telegram</label>
                <input type="text" value={settings.telegram}
                  onChange={e => setSettings(prev => ({ ...prev, telegram: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="@yourshop" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 广告图管理 */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-semibold">广告图管理</h3>
            <p className="text-sm text-gray-500 mt-0.5">H5 首页轮播展示，建议尺寸 750×300px</p>
          </div>
          <button onClick={() => {
            setShowBannerForm(true);
            setBannerForm({ image_url: '', title: '', link_url: '', sort_order: banners.length });
            setBannerFile(null);
            setBannerPreview('');
          }}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
            <Plus className="w-4 h-4" />
            <span>添加广告图</span>
          </button>
        </div>

        {banners.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>暂无广告图，点击右上角添加</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {banners.map(banner => (
              <div key={banner.id}
                className={`border rounded-xl overflow-hidden ${banner.status === 'inactive' ? 'opacity-50' : ''}`}>
                <div className="h-32 bg-gray-100 overflow-hidden">
                  <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium truncate">{banner.title || '（无标题）'}</p>
                  {banner.link_url && (
                    <p className="text-xs text-blue-500 truncate mt-1">{banner.link_url}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <button onClick={() => toggleBannerStatus(banner)}
                      className={`text-xs px-2 py-1 rounded-full ${
                        banner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                      {banner.status === 'active' ? '显示中' : '已隐藏'}
                    </button>
                    <button onClick={() => handleDeleteBanner(banner.id)}
                      className="text-red-500 hover:text-red-700 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 添加广告图表单 */}
        {showBannerForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b">
                <h3 className="text-lg font-bold">添加广告图</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">广告图片 *</label>
                  <div
                    className="h-40 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer"
                    onClick={() => document.getElementById('bannerUpload').click()}>
                    {bannerPreview
                      ? <img src={bannerPreview} alt="preview" className="w-full h-full object-cover" />
                      : <div className="text-center">
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-400">点击上传广告图</p>
                          <p className="text-xs text-gray-300">建议 750×300px</p>
                        </div>}
                  </div>
                  <input type="file" id="bannerUpload" accept="image/*"
                    onChange={handleBannerFileChange} className="hidden" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">标题（选填）</label>
                  <input type="text" value={bannerForm.title}
                    onChange={e => setBannerForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例：新品上市" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">跳转链接（选填）</label>
                  <input type="text" value={bannerForm.link_url}
                    onChange={e => setBannerForm(prev => ({ ...prev, link_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="/pages/xxx/index" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">排序（数字越小越靠前）</label>
                  <input type="number" value={bannerForm.sort_order}
                    onChange={e => setBannerForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="p-6 border-t flex space-x-3">
                <button onClick={() => setShowBannerForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  取消
                </button>
                <button onClick={handleAddBanner} disabled={savingBanner || uploadingBanner}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {uploadingBanner ? '上传中...' : savingBanner ? '保存中...' : '确认添加'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopSettingsPage;